import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '@/context/AuthContext';

const WS_URL = process.env.REACT_APP_BACKEND_URL?.replace('https://', 'wss://').replace('http://', 'ws://');
const SOUND_URL = '/sounds/nova-entrega.mp3';
const SOUND_LOOP_INTERVAL_MS = 3000; // Toca o MP3 a cada 3 segundos até ser aceito
const MUTE_STORAGE_KEY = 'rota017_muted';

export const useNotifications = () => {
  const { empresa, usuario } = useAuth();
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [muted, setMuted] = useState(() => {
    try { return localStorage.getItem(MUTE_STORAGE_KEY) === 'true'; } catch { return false; }
  });
  
  const wsRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;
  const audioRef = useRef(null);
  const loopTimerRef = useRef(null);
  const pendingEntregasRef = useRef(new Set()); // Entregas anunciadas ainda não aceitas
  
  const shouldConnect = usuario?.perfil === 'entregador' && empresa?.id;
  
  // Lazily cria o objeto Audio (só depois do primeiro user gesture do browser)
  const getAudio = useCallback(() => {
    if (!audioRef.current) {
      const audio = new Audio(SOUND_URL);
      audio.preload = 'auto';
      audioRef.current = audio;
    }
    return audioRef.current;
  }, []);
  
  const stopSound = useCallback(() => {
    if (loopTimerRef.current) {
      clearInterval(loopTimerRef.current);
      loopTimerRef.current = null;
    }
    if (audioRef.current) {
      try { audioRef.current.pause(); audioRef.current.currentTime = 0; } catch { /* noop */ }
    }
    setIsPlaying(false);
  }, []);
  
  const startSoundLoop = useCallback(() => {
    if (muted) return;
    if (loopTimerRef.current) return; // Já está em loop
    const audio = getAudio();
    const play = () => {
      try {
        audio.currentTime = 0;
        const p = audio.play();
        if (p && p.catch) p.catch(() => { /* Autoplay bloqueado: silenciar até o próx gesto */ });
      } catch { /* noop */ }
    };
    play(); // Toca imediatamente
    loopTimerRef.current = setInterval(play, SOUND_LOOP_INTERVAL_MS);
    setIsPlaying(true);
  }, [muted, getAudio]);
  
  const toggleMute = useCallback(() => {
    setMuted(prev => {
      const next = !prev;
      try { localStorage.setItem(MUTE_STORAGE_KEY, String(next)); } catch { /* noop */ }
      if (next) stopSound();
      else if (pendingEntregasRef.current.size > 0) startSoundLoop();
      return next;
    });
  }, [stopSound, startSoundLoop]);
  
  const handleEntregaAtendida = useCallback((entregaId) => {
    pendingEntregasRef.current.delete(entregaId);
    if (pendingEntregasRef.current.size === 0) stopSound();
  }, [stopSound]);
  
  const connect = useCallback(() => {
    if (!shouldConnect || wsRef.current?.readyState === WebSocket.OPEN) return;
    
    try {
      const wsUrl = `${WS_URL}/api/ws/entregador/${empresa.id}`;
      wsRef.current = new WebSocket(wsUrl);
      
      wsRef.current.onopen = () => {
        setIsConnected(true);
        reconnectAttempts.current = 0;
      };
      
      wsRef.current.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          
          if (data.type === 'nova_entrega_aberta') {
            const entregaId = data.data?.entrega_id;
            if (entregaId) pendingEntregasRef.current.add(entregaId);
            
            const notification = {
              id: Date.now(),
              type: data.type,
              message: data.message,
              data: data.data,
              timestamp: new Date(),
              read: false,
            };
            setNotifications(prev => [notification, ...prev.slice(0, 49)]);
            setUnreadCount(prev => prev + 1);
            
            startSoundLoop();
            showBrowserNotification(data.message, data.data);
          } else if (data.type === 'entrega_aceita' || data.type === 'entrega_atribuida') {
            // Outro entregador aceitou; para o som se essa era a última pendente
            const entregaId = data.data?.entrega_id;
            if (entregaId) handleEntregaAtendida(entregaId);
          }
        } catch (e) {
          console.error('Erro ao processar mensagem:', e);
        }
      };
      
      wsRef.current.onclose = () => {
        setIsConnected(false);
        if (reconnectAttempts.current < maxReconnectAttempts && shouldConnect) {
          const delay = Math.min(1000 * Math.pow(2, reconnectAttempts.current), 30000);
          reconnectAttempts.current++;
          reconnectTimeoutRef.current = setTimeout(connect, delay);
        }
      };
      
      wsRef.current.onerror = () => { /* onclose fará o reconnect */ };
    } catch (error) {
      console.error('Erro ao criar WebSocket:', error);
    }
  }, [shouldConnect, empresa?.id, startSoundLoop, handleEntregaAtendida]);
  
  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    stopSound();
    setIsConnected(false);
  }, [stopSound]);
  
  const markAsRead = useCallback((notificationId) => {
    setNotifications(prev => prev.map(n => n.id === notificationId ? { ...n, read: true } : n));
    setUnreadCount(prev => Math.max(0, prev - 1));
  }, []);
  
  const markAllAsRead = useCallback(() => {
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  }, []);
  
  const clearNotifications = useCallback(() => {
    setNotifications([]);
    setUnreadCount(0);
    pendingEntregasRef.current.clear();
    stopSound();
  }, [stopSound]);
  
  useEffect(() => {
    if (shouldConnect) {
      requestNotificationPermission();
      connect();
    }
    return () => { disconnect(); };
  }, [shouldConnect, connect, disconnect]);
  
  useEffect(() => {
    if (!isConnected) return;
    const pingInterval = setInterval(() => {
      if (wsRef.current?.readyState === WebSocket.OPEN) wsRef.current.send('ping');
    }, 30000);
    return () => clearInterval(pingInterval);
  }, [isConnected]);
  
  return {
    isConnected,
    notifications,
    unreadCount,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    reconnect: connect,
    muted,
    toggleMute,
    isPlaying,
    stopSound,
    handleEntregaAtendida, // permite que o próprio entregador pare o som ao aceitar
  };
};

const requestNotificationPermission = async () => {
  if ('Notification' in window && Notification.permission === 'default') {
    await Notification.requestPermission();
  }
};

const showBrowserNotification = (title, data) => {
  if ('Notification' in window && Notification.permission === 'granted') {
    const notification = new Notification('Rota 017 - ' + title, {
      body: data?.destino_endereco || 'Nova entrega disponível',
      icon: '/favicon.ico',
      tag: 'nova-entrega-' + data?.entrega_id,
      requireInteraction: true,
    });
    notification.onclick = () => { window.focus(); notification.close(); };
    setTimeout(() => notification.close(), 10000);
  }
};

export default useNotifications;
